import { useState } from 'react';
import { createPortal } from 'react-dom';

import { StatusGlyph } from '@/shared/ui/components';
import { STATUS_GLYPH, STATUS_LABELS, type AppointmentStatusType } from '@/components/atoms/status-badge';

/**
 * StatusDot — an appointment status as its glyph alone (StatusBadge without the label), for tight
 * places like the week view's tiles. It explains itself (Antonin, 12 Oct; one rule for every Scrips
 * app): hovering shows an instant tooltip (no delay, no fade) with the status and what it means, in
 * the rail's inverted style. Portalled to <body> so no tile clips it and no sticky header covers it.
 */
const MEANING: Record<AppointmentStatusType, string> = {
  booked: 'Booked, not confirmed yet',
  confirmed: 'Confirmed, not arrived yet',
  waitlisted: 'Waitlist, proposed or pending',
  arrived: 'Arrived at the clinic',
  checkedIn: 'Checked in, ready to be seen',
  fulfilled: 'Visit over',
  noShow: 'Did not come',
  cancelled: 'Cancelled or entered in error',
};

export function StatusDot({ status }: { status: AppointmentStatusType }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const label = STATUS_LABELS[status];
  return (
    <span
      data-status-dot={status}
      role="img"
      aria-label={`${label}: ${MEANING[status]}`}
      className="inline-flex"
      onMouseEnter={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setAt({ x: r.left + r.width / 2, y: r.top });
      }}
      onMouseLeave={() => setAt(null)}
    >
      <StatusGlyph kind={STATUS_GLYPH[status] ?? 'empty'} />
      {at &&
        createPortal(
          <span
            role="tooltip"
            className="pointer-events-none fixed z-[300] -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-input bg-[var(--tooltip-bg)] px-2 py-1 text-left text-xs font-medium text-[var(--tooltip-fg)]"
            // Anchored over the glyph; measured at runtime, not a design value.
            style={{ left: at.x, top: at.y - 8 }}
          >
            <span className="block">{label}</span>
            <span className="block font-normal text-[var(--tooltip-fg-2)]">{MEANING[status]}</span>
          </span>,
          document.body,
        )}
    </span>
  );
}
